import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Search,
  Plus,
  Store as StoreIcon,
  Mail,
  Phone,
  Power,
  X,
  Loader2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import {
  CREATE_CUSTOM_RETAILER,
  GET_CUSTOM_RETAILERS,
  SET_CUSTOM_RETAILER_STATUS,
} from '../graphql/queries';

interface Retailer {
  id: string;
  name: string;
  contactPerson: string | null;
  emailId: string | null;
  mobileNumber: string | null;
  address: string | null;
  city: string | null;
  pincode: string | null;
  gstin: string | null;
  tags: string[];
  status: boolean;
  createdAt: string | null;
}

interface CustomRetailersProps {
  showToast?: (msg: string, type?: 'success' | 'error' | 'info') => void;
}

const PAGE_SIZE = 25;

export const CustomRetailers: React.FC<CustomRetailersProps> = ({ showToast }) => {
  const notify = (msg: string, type: 'success' | 'error' | 'info' = 'success') => {
    if (showToast) showToast(msg, type);
  };

  const [search, setSearch] = useState('');
  const [pageNo, setPageNo] = useState(0);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [showCreate, setShowCreate] = useState(false);

  const variables = useMemo(
    () => ({
      search: search.trim() || undefined,
      status: statusFilter === 'all' ? undefined : statusFilter === 'active',
      pageNo,
      limit: PAGE_SIZE,
    }),
    [search, statusFilter, pageNo]
  );

  const { data, loading, refetch } = useQuery(GET_CUSTOM_RETAILERS, {
    variables,
    fetchPolicy: 'cache-and-network',
  });

  const [setStatus] = useMutation(SET_CUSTOM_RETAILER_STATUS);

  const items: Retailer[] = data?.getCustomRetailers?.items || [];
  const total: number = data?.getCustomRetailers?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const onToggle = async (row: Retailer) => {
    try {
      const { data } = await setStatus({
        variables: { id: row.id, status: !row.status },
      });
      const r = data?.setCustomRetailerStatus;
      if (r?.error) {
        notify(r.message || 'Failed to update', 'error');
      } else {
        notify(r?.message || 'Status updated');
        refetch();
      }
    } catch (e: any) {
      notify(e?.message || 'Failed', 'error');
    }
  };

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-text">Retailers</h1>
          <p className="text-sm text-text2">Your manually-managed retailer directory.</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-primary">
          <Plus size={16} />
          New Retailer
        </button>
      </header>

      <div className="card p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text3" />
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPageNo(0);
              }}
              placeholder="Search by name, contact, mobile, email, city…"
              className="input-field pl-9"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as any);
              setPageNo(0);
            }}
            className="input-field max-w-[140px]"
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        <div className="overflow-x-auto -mx-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-text3 border-b border-border">
                <th className="px-4 py-3">Retailer</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">City</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-text3">
                    <Loader2 className="inline mr-2 animate-spin" size={14} />
                    Loading retailers…
                  </td>
                </tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-text3">
                    No retailers yet. Click "New Retailer" to add one.
                  </td>
                </tr>
              )}
              {items.map((row) => (
                <tr key={row.id} className="border-b border-[#F8FAFC] hover:bg-[#F8FAFC]/60">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                        <StoreIcon size={15} />
                      </div>
                      <div className="leading-tight">
                        <div className="font-bold text-text">{row.name}</div>
                        {row.contactPerson && (
                          <div className="text-[11px] text-text3">{row.contactPerson}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="leading-tight text-[12px] text-text2">
                      {row.emailId && (
                        <div className="flex items-center gap-1">
                          <Mail size={11} /> {row.emailId}
                        </div>
                      )}
                      {row.mobileNumber && (
                        <div className="flex items-center gap-1">
                          <Phone size={11} /> {row.mobileNumber}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-text2">
                    {row.city ? (
                      <>
                        {row.city}
                        {row.pincode ? <span className="text-text3"> · {row.pincode}</span> : null}
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`status-pill ${
                        row.status
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-red-50 text-red-700'
                      }`}
                    >
                      {row.status ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => onToggle(row)}
                        title={row.status ? 'Deactivate' : 'Activate'}
                        className={`h-8 px-3 rounded-lg text-[12px] font-semibold flex items-center gap-1 ${
                          row.status
                            ? 'bg-red-50 text-red-700 hover:bg-red-100'
                            : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                        }`}
                      >
                        <Power size={12} /> {row.status ? 'Deactivate' : 'Activate'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between pt-4">
            <span className="text-[12px] text-text3">
              Page {pageNo + 1} of {totalPages} · {total} retailers
            </span>
            <div className="flex items-center gap-2">
              <button
                disabled={pageNo === 0}
                onClick={() => setPageNo((p) => Math.max(0, p - 1))}
                className="h-8 px-3 rounded-lg border border-border text-[12px] font-semibold text-text2 hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1"
              >
                <ChevronLeft size={13} /> Prev
              </button>
              <button
                disabled={pageNo + 1 >= totalPages}
                onClick={() => setPageNo((p) => p + 1)}
                className="h-8 px-3 rounded-lg border border-border text-[12px] font-semibold text-text2 hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1"
              >
                Next <ChevronRight size={13} />
              </button>
            </div>
          </div>
        )}
      </div>

      {showCreate && (
        <CreateRetailerModal
          onClose={() => setShowCreate(false)}
          onCreated={(msg) => {
            notify(msg);
            setShowCreate(false);
            refetch();
          }}
          onError={(msg) => notify(msg, 'error')}
        />
      )}
    </div>
  );
};

const CreateRetailerModal: React.FC<{
  onClose: () => void;
  onCreated: (message: string) => void;
  onError: (message: string) => void;
}> = ({ onClose, onCreated, onError }) => {
  const [form, setForm] = useState({
    name: '',
    contactPerson: '',
    emailId: '',
    mobileNumber: '',
    address: '',
    city: '',
    pincode: '',
    gstin: '',
  });
  const [createRetailer, { loading }] = useMutation(CREATE_CUSTOM_RETAILER);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const { data } = await createRetailer({ variables: { input: form } });
      const r = data?.createCustomRetailer;
      if (r?.error) {
        onError(r.message || 'Failed to create retailer');
        return;
      }
      onCreated(r?.message || 'Retailer created');
    } catch (e: any) {
      onError(e?.message || 'Failed to create retailer');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/40">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl animate-modal-in">
        <header className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <h2 className="text-lg font-black text-text">New Retailer</h2>
            <p className="text-[11px] text-text3">Add a retailer to your directory.</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
            <X size={16} />
          </button>
        </header>

        <form onSubmit={submit} className="p-6 grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="input-label">Retailer Name *</label>
            <input
              className="input-field"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Sharma Stores"
              autoFocus
            />
          </div>
          <div>
            <label className="input-label">Contact Person</label>
            <input
              className="input-field"
              value={form.contactPerson}
              onChange={(e) => setForm({ ...form, contactPerson: e.target.value })}
            />
          </div>
          <div>
            <label className="input-label">Mobile</label>
            <input
              className="input-field"
              value={form.mobileNumber}
              onChange={(e) => setForm({ ...form, mobileNumber: e.target.value })}
              placeholder="9876543210"
            />
          </div>
          <div className="col-span-2">
            <label className="input-label">Email</label>
            <input
              type="email"
              className="input-field"
              value={form.emailId}
              onChange={(e) => setForm({ ...form, emailId: e.target.value })}
            />
          </div>
          <div>
            <label className="input-label">City</label>
            <input
              className="input-field"
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
            />
          </div>
          <div>
            <label className="input-label">Pincode</label>
            <input
              className="input-field"
              value={form.pincode}
              onChange={(e) => setForm({ ...form, pincode: e.target.value })}
            />
          </div>
          <div className="col-span-2">
            <label className="input-label">Address</label>
            <textarea
              className="input-field h-[60px] py-2"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              rows={2}
            />
          </div>
          <div className="col-span-2">
            <label className="input-label">GSTIN</label>
            <input
              className="input-field"
              value={form.gstin}
              onChange={(e) => setForm({ ...form, gstin: e.target.value })}
            />
          </div>

          <div className="col-span-2 flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Creating…' : 'Create Retailer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
